import React, { useEffect, useRef, useState } from 'react'
import Cookies from 'js-cookie'
import {
  Modal,
  ModalContent,
  ModalClose,
  Button,
  IconLogoCombined,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { FREE_TRIAL_I18N_NAMESPACE } from '../../../i18n'
import { useFreeTrial } from '../../../lib'

export const TrialNoLongerAvailableModal = () => {
  const [show, setShow] = useState<boolean>(false)
  const { t } = useTranslation(FREE_TRIAL_I18N_NAMESPACE)
  const { isTrialNoLongerAvailable, ineligibilityReason } =
    useIsTrialNoLongerAvailable()
  const handleClose = () => {
    setShow(false)
    // Reload the page to get the right experimentation context for the user (E.G: Promo is not active
    // for free trial users, so reloading the page will enable them to see the promo)
    window.location.reload()
  }

  useEffect(() => {
    if (isTrialNoLongerAvailable) {
      setShow(true)

      Cookies.remove('referral_token')
      Cookies.remove('referral_token_no_longer_valid_error')
      Cookies.remove('referral_ineligibility_reason')
    }
  }, [isTrialNoLongerAvailable])

  const subtitle = ineligibilityReason
    ? t(`common.shared.modal.noLongerAvailable.reasons.${ineligibilityReason}`)
    : t('common.shared.modal.noLongerAvailable.subtitle')

  return (
    <Modal size='small' show={show} onClose={handleClose}>
      <ModalContent
        aria-label={t('common.shared.modal.noLongerAvailable.title')}
        className='mc-bg-light'
      >
        <div className='mc-theme-light mc-px-5 mc-py-9 mc-text--center'>
          <ModalClose />
          <IconLogoCombined
            className='mc-mb-4 mc-text-color--dark mc-icon--scale-11'
            viewBox='0 0 66 50'
          />

          <h1 className='mc-mb-3 mc-text-h4'>
            {t('common.shared.modal.noLongerAvailable.title')}
          </h1>

          <p className='mc-mb-8 mc-px-6'>{subtitle}</p>

          <Button accessibleWhenDisabled fullWidth onClick={handleClose}>
            {t('common.shared.modal.noLongerAvailable.cta')}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  )
}

const INELIGIBILITY_REASONS = [
  'invalid_token',
  'already_redeemed',
  'campaign_ended',
  'partnership_disabled',
  'advocate_limit_reached',
  'not_eligible',
] as const

const knownIneligibilityReason = (reason?: string | null) =>
  INELIGIBILITY_REASONS.find((known) => known === reason)

const useIsTrialNoLongerAvailable = () => {
  const { trial } = useFreeTrial()
  const carriedReason = useRef(
    knownIneligibilityReason(Cookies.get('referral_ineligibility_reason')),
  )
  const referralTokenCookie = Cookies.get('referral_token')
  const referralTokenNoLongerValidErrorCookie = Cookies.get(
    'referral_token_no_longer_valid_error',
  )

  const isTrialNoLongerAvailable =
    !trial?.eligible &&
    (!!referralTokenCookie || !!referralTokenNoLongerValidErrorCookie)

  return {
    isTrialNoLongerAvailable,
    ineligibilityReason:
      knownIneligibilityReason(trial?.ineligibilityReason) ??
      carriedReason.current,
  }
}
